'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Pause, Play } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@kiao-voice/ui-kit'
import {
  FILE_NAME,
  INITIAL_POSITION_SECS,
  MEETING_CUES,
  MEETING_SPEAKERS,
  PLAYBACK_END_SECS,
  TOTAL_LABEL,
  WINDOW_SECS,
} from './meeting-demo-cues'
import {
  activeCueIndexAt,
  buildWaveCells,
  formatClock,
  formatCueTimecode,
  rulerTicks,
  speakerBlocks,
  waveBars,
} from './meeting-demo-model'
import { useDemoStarted } from '@/lib/analytics/use-demo-started'

const WAVE_HEIGHT = 48
const TICK_MS = 100
const STEP_SECS = 0.1

function paint(canvas: HTMLCanvasElement | null, width: number, color: string) {
  if (!canvas) return
  const dpr = Math.min(2, window.devicePixelRatio || 1)
  canvas.width = Math.round(width * dpr)
  canvas.height = Math.round(WAVE_HEIGHT * dpr)
  canvas.style.width = `${width}px`
  canvas.style.height = `${WAVE_HEIGHT}px`
  // jsdom 回 null（vitest.setup 刻意這樣 stub），真實瀏覽器在不支援的 context
  // 型別上也回 null——兩邊都只要安靜跳過，不要 throw。
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, width, WAVE_HEIGHT)
  ctx.fillStyle = color
  const mid = WAVE_HEIGHT / 2
  for (const bar of waveBars(width, CELLS, { span: WAVE_HEIGHT - 2 })) {
    ctx.fillRect(bar.x, mid - bar.h / 2, 2, bar.h)
  }
}

const CELLS = buildWaveCells(MEETING_CUES)

/**
 * 會議示範：上方逐句、下方播放列（波形＋講者段落塊＋刻度尺）。**不會發出聲音**，
 * 播放只是把時間往前推。
 *
 * 波形是兩張同尺寸的 canvas 疊著：下層淡、上層濃，上層包在一個依播放進度裁寬的
 * 外框裡。播放時只改那一個寬度（`--pos`），canvas 一次都不重畫——每 100ms 重畫
 * 一張波形會讓整頁掉影格。
 */
export function MeetingEditorDemo() {
  const { t } = useTranslation(['web'])
  const [position, setPosition] = useState(INITIAL_POSITION_SECS)
  const [playing, setPlaying] = useState(false)
  const markDemoStarted = useDemoStarted('meeting_editor')
  const waveRef = useRef<HTMLDivElement | null>(null)
  const idleCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const playedCanvasRef = useRef<HTMLCanvasElement | null>(null)

  const blocks = useMemo(() => speakerBlocks(MEETING_CUES), [])
  const ticks = useMemo(() => rulerTicks(), [])
  const activeIndex = activeCueIndexAt(position, MEETING_CUES)
  const positionPct = `${((position / WINDOW_SECS) * 100).toFixed(3)}%`

  const redraw = useCallback(() => {
    const width = Math.round(waveRef.current?.getBoundingClientRect().width ?? 0)
    if (width < 10) return
    paint(idleCanvasRef.current, width, 'color-mix(in oklch, var(--fg) 20%, transparent)')
    paint(playedCanvasRef.current, width, 'color-mix(in oklch, var(--fg) 58%, transparent)')
  }, [])

  useEffect(() => {
    redraw()
    window.addEventListener('resize', redraw)
    return () => window.removeEventListener('resize', redraw)
  }, [redraw])

  useEffect(() => {
    if (!playing) return
    // 用 functional updater 而不是讀 ref：100ms 的 tick 會被 React 批次處理，
    // 讀外部值的版本會讓同一批的每一次 tick 都算出同一個「下一格」，時鐘就
    // 只前進一格。
    const timer = setInterval(() => {
      setPosition((current) => Math.min(PLAYBACK_END_SECS, current + STEP_SECS))
    }, TICK_MS)
    return () => clearInterval(timer)
  }, [playing])

  // 走到終點就停。獨立一個 effect，而不是塞進 setPosition 的 updater——
  // updater 依約必須是純函式，在 StrictMode 的雙呼叫下副作用會跑兩次。
  useEffect(() => {
    if (playing && position >= PLAYBACK_END_SECS) setPlaying(false)
  }, [playing, position])

  function togglePlay() {
    markDemoStarted()
    // 先算出要變成什麼，再各自 set。updater 依約必須是純函式——這裡是使用者
    // 點一下，讀 `position` 不會有批次 tick 的問題，但把 `setPosition` 藏在
    // `setPlaying` 的 updater 裡仍然是副作用，StrictMode 的雙呼叫下會跑兩次。
    if (playing) {
      setPlaying(false)
      return
    }
    // 播到尾了再按一次就從頭播，不是卡在終點。
    const restart = position >= PLAYBACK_END_SECS
    if (restart) setPosition(0)
    setPlaying(true)
  }

  return (
    <div className="overflow-hidden rounded-[14px] border border-border bg-background">
      <div className="flex flex-wrap items-baseline justify-between gap-(--gap-md) border-b border-line-soft px-[22px] pt-4 pb-3 max-[860px]:px-3.5">
        <p className="text-md font-semibold">{FILE_NAME}</p>
        <p className="inline-flex items-center gap-1.5 text-sm text-muted">
          <span aria-hidden="true" className="size-[7px] rounded-full bg-ok" />
          {t('web:transcriptionPage.meetings.status', { total: TOTAL_LABEL })}
        </p>
      </div>

      <ol
        aria-label={t('web:transcriptionPage.meetings.cuesAria')}
        className="relative max-h-[348px] list-none overflow-y-auto overscroll-contain py-1 max-[860px]:max-h-[320px]"
      >
        {MEETING_CUES.map((cue, index) => {
          const current = index === activeIndex
          return (
            <li key={index}>
              <button
                type="button"
                onClick={() => {
                  markDemoStarted()
                  setPosition(cue.start)
                }}
                aria-current={current ? 'true' : undefined}
                aria-label={t('web:transcriptionPage.meetings.jumpToAria', {
                  time: formatCueTimecode(cue.start),
                })}
                className={cn(
                  'relative grid w-full cursor-pointer gap-[5px] bg-transparent px-[22px] py-3 pl-[26px] text-left transition-colors hover:bg-fg-soft focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-foreground max-[860px]:px-3.5 max-[860px]:py-[11px] max-[860px]:pl-[18px]',
                  index === 0 ? 'border-t-0' : 'border-t border-line-soft',
                  current &&
                    'bg-brand-soft before:absolute before:top-[-1px] before:bottom-0 before:left-0 before:w-[3px] before:bg-brand before:content-[""]'
                )}
              >
                <span className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-[7px] text-xs-plus font-semibold">
                    <span
                      aria-hidden="true"
                      className={cn(
                        'size-2.5 rounded-full',
                        cue.speaker === 2
                          ? 'bg-spk-2'
                          : 'bg-spk-1 shadow-[inset_0_0_0_1px_color-mix(in_oklch,var(--fg)_22%,transparent)]'
                      )}
                    />
                    {MEETING_SPEAKERS[cue.speaker]}
                  </span>
                  <span className="font-mono text-2xs-plus text-muted [font-variant-numeric:tabular-nums]">
                    {formatCueTimecode(cue.start)} <span className="opacity-55">→</span>{' '}
                    {formatCueTimecode(cue.end)}
                  </span>
                </span>
                <span className="block text-md leading-[1.7]">{cue.text}</span>
              </button>
            </li>
          )
        })}
      </ol>

      <div className="grid gap-[9px] border-t border-border px-[22px] pt-3 pb-4 shadow-[0_-20px_44px_-30px_color-mix(in_oklch,var(--fg)_34%,transparent)] max-[860px]:px-3.5">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={togglePlay}
            aria-label={t(
              playing ? 'web:transcriptionPage.meetings.pause' : 'web:transcriptionPage.meetings.play'
            )}
            className="grid size-[42px] flex-none cursor-pointer place-items-center rounded-full border-0 bg-foreground text-background transition-colors hover:bg-[color-mix(in_oklch,var(--fg)_82%,var(--bg))] focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-foreground"
          >
            {playing ? (
              <Pause className="size-4 fill-current" strokeWidth={0} />
            ) : (
              <Play className="size-4 fill-current" strokeWidth={0} />
            )}
          </button>
          <span className="font-mono text-xs-plus text-muted [font-variant-numeric:tabular-nums]">
            <b className="font-medium text-foreground">{formatClock(position)}</b> / {TOTAL_LABEL}
          </span>
          <span className="ml-auto font-mono text-2xs text-muted max-[860px]:hidden">
            {t('web:transcriptionPage.meetings.zoomLabel')}
          </span>
        </div>

        <div className="grid gap-[7px]" style={{ '--pos': positionPct } as React.CSSProperties}>
          <div
            ref={waveRef}
            aria-hidden="true"
            className="relative h-12 before:absolute before:inset-x-0 before:top-1/2 before:z-[2] before:h-px before:bg-line-soft before:content-['']"
          >
            <canvas ref={idleCanvasRef} className="absolute top-0 left-0 block" />
            <div className="absolute top-0 bottom-0 left-0 z-[1] w-(--pos) overflow-hidden">
              <canvas ref={playedCanvasRef} className="absolute top-0 left-0 block" />
            </div>
            <span className="absolute top-[-4px] bottom-[-2px] left-(--pos) z-[3] w-[1.5px] bg-brand before:absolute before:top-[-2px] before:left-1/2 before:size-[7px] before:-translate-x-1/2 before:rounded-full before:bg-brand before:content-['']" />
          </div>

          <div aria-hidden="true" className="relative h-[34px]">
            {blocks.map((block) => {
              const current = block.index === activeIndex
              return (
                <span
                  key={block.index}
                  style={
                    {
                      '--left': `${block.leftPct.toFixed(3)}%`,
                      '--width': `${block.widthPct.toFixed(3)}%`,
                    } as React.CSSProperties
                  }
                  className={cn(
                    'absolute top-0 bottom-0 left-(--left) flex w-(--width) items-center overflow-hidden @container',
                    "before:absolute before:inset-y-0 before:inset-x-px before:rounded-[5px] before:content-['']",
                    current
                      ? 'before:bg-press-stroke'
                      : block.speaker === 2
                        ? 'before:bg-spk-2'
                        : 'before:bg-spk-1 before:shadow-[inset_0_0_0_1px_color-mix(in_oklch,var(--fg)_8%,transparent)]'
                  )}
                >
                  <span
                    className={cn(
                      // 11px／9.5px 刻意不進字級 scale：它們是時間軸「圖形」上的
                      // 標籤（段落塊名字、刻度秒數），不是行文。進 scale 等於宣稱
                      // 有元件可以用 11px 寫字，而那會被下一個人拿去寫內文。
                      'relative truncate pl-2.5 text-[11px] font-medium @max-[64px]:px-1.5',
                      current && 'text-background'
                    )}
                  >
                    {block.name}
                  </span>
                </span>
              )
            })}
          </div>

          <div aria-hidden="true" className="relative h-[13px]">
            {ticks.map((tick) => (
              <span
                key={tick.label}
                style={{ '--left': `${tick.leftPct.toFixed(3)}%` } as React.CSSProperties}
                className="absolute top-0 left-(--left) pl-[5px] font-mono text-[9.5px] leading-[1.25] whitespace-nowrap text-muted before:absolute before:top-0 before:left-0 before:h-[5px] before:w-px before:bg-border before:content-['']"
              >
                {tick.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
